// src/pages/Public/ChatSpace/PublicChatSpace.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    Avatar,
    CircularProgress,
    Snackbar,
    Alert,
    TextField,
    Button,
    useMediaQuery,
    useTheme,
    Switch,
    FormControlLabel,
    Drawer,
    InputAdornment,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ForumIcon from '@mui/icons-material/Forum';
import MenuIcon from '@mui/icons-material/Menu';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LoginIcon from '@mui/icons-material/Login';
import ChatSpaceMessageBubble from '../../ChatSpace/ChatSpaceMessageBubble';
import ChatSpaceCitations from '../../ChatSpace/ChatSpaceCitations';
import axios from 'axios';
import backendURL from '../../../../config';

// ============================================================
// GUEST REGISTRATION FORM
// ============================================================
function GuestRegistrationForm({ settings, onRegister, isRegistering }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) return;
        onRegister(name.trim(), email.trim());
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                bgcolor: 'grey.100',
                p: 2,
            }}
        >
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    p: 4,
                    maxWidth: 420,
                    width: '100%',
                    boxShadow: 3,
                    textAlign: 'center',
                }}
            >
                <Avatar
                    sx={{
                        width: 56,
                        height: 56,
                        bgcolor: settings?.primary_color || '#3b82f6',
                        color: settings?.secondary_color || '#ffffff',
                        mx: 'auto',
                        mb: 2,
                        borderRadius: 3,
                    }}
                >
                    <AutoAwesomeIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                    {settings?.chatbot_name || 'Chat Space'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter your details to start chatting
                </Typography>

                <TextField
                    fullWidth
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isRegistering}
                    required
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <PersonIcon sx={{ color: 'grey.400' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isRegistering}
                    required
                    sx={{ mb: 3 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailIcon sx={{ color: 'grey.400' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={isRegistering || !name.trim() || !email.trim()}
                    startIcon={isRegistering ? <CircularProgress size={18} /> : <LoginIcon />}
                    sx={{
                        bgcolor: settings?.primary_color || '#3b82f6',
                        '&:hover': { bgcolor: settings?.primary_color || '#3b82f6', filter: 'brightness(0.9)' },
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.5,
                    }}
                >
                    {isRegistering ? 'Starting...' : 'Start Chat'}
                </Button>
            </Box>
        </Box>
    );
}

// ============================================================
// PUBLIC SIDEBAR
// ============================================================
function PublicSidebar({ isOpen, isCollapsed, onToggle, onConversationSelect, currentSessionCode, isMobile, sessionToken, guestName }) {
    const [conversations, setConversations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && sessionToken) loadConversations();
    }, [isOpen, sessionToken]);

    useEffect(() => {
        if (isOpen && currentSessionCode && sessionToken) loadConversations();
    }, [currentSessionCode]);

    const loadConversations = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${backendURL}/api/public/chatspace/conversations`, {
                params: { session_token: sessionToken },
            });
            if (response.data.success) {
                setConversations(response.data.data);
            }
        } catch (error) {
            console.error('Failed to load conversations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNewChat = () => {
        onConversationSelect(null);
        if (isMobile) onToggle();
    };

    const handleSelect = (sessionCode) => {
        onConversationSelect(sessionCode);
        if (isMobile) onToggle();
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        const mins = Math.floor(diff / 60000);
        const hrs = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);
        if (mins < 60) return `${mins}m ago`;
        if (hrs < 24) return `${hrs}h ago`;
        if (days < 7) return `${days}d ago`;
        return date.toLocaleDateString();
    };

    const filtered = conversations.filter((c) =>
        c.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const sidebarContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                {!isCollapsed && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="subtitle2" fontWeight={600}>Chats</Typography>
                    </Box>
                )}
                <IconButton size="small" onClick={onToggle}>
                    <MenuOpenIcon
                        sx={{
                            fontSize: 18,
                            color: 'grey.500',
                            transform: isCollapsed ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.3s',
                        }}
                    />
                </IconButton>
            </Box>

            {/* New Chat */}
            <Box sx={{ p: 1.5, px: isCollapsed ? 1 : 1.5 }}>
                {isCollapsed ? (
                    <IconButton
                        onClick={handleNewChat}
                        color="primary"
                        sx={{
                            width: '100%',
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'primary.dark' },
                        }}
                    >
                        <AddIcon />
                    </IconButton>
                ) : (
                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleNewChat}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
                    >
                        New Chat
                    </Button>
                )}
            </Box>

            {/* Search */}
            {!isCollapsed && (
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 16, color: 'grey.400' }} />
                                </InputAdornment>
                            ),
                            sx: { fontSize: '0.75rem', borderRadius: 2 },
                        }}
                    />
                </Box>
            )}

            {/* Conversations */}
            <Box sx={{ flex: 1, overflow: 'auto', px: isCollapsed ? 0.5 : 1 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : filtered.length === 0 ? (
                    !isCollapsed && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                            <Typography variant="caption" color="text.secondary" display="block">
                                {searchQuery ? 'No conversations found' : 'No conversations yet'}
                            </Typography>
                        </Box>
                    )
                ) : (
                    filtered.map((conv) => (
                        <Box
                            key={conv.session_code}
                            onClick={() => handleSelect(conv.session_code)}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                p: isCollapsed ? 1 : 1.5,
                                mb: 0.5,
                                borderRadius: 2,
                                cursor: 'pointer',
                                justifyContent: isCollapsed ? 'center' : 'flex-start',
                                bgcolor: conv.session_code === currentSessionCode ? 'primary.lighter' : 'transparent',
                                '&:hover': {
                                    bgcolor: conv.session_code === currentSessionCode ? 'primary.lighter' : 'action.hover',
                                },
                                transition: 'background-color 0.15s',
                            }}
                        >
                            <ChatBubbleOutlineIcon
                                sx={{
                                    fontSize: 14,
                                    color: conv.session_code === currentSessionCode ? 'primary.main' : 'grey.400',
                                    mt: 0.25,
                                    flexShrink: 0,
                                }}
                            />
                            {!isCollapsed && (
                                <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                    <Typography variant="caption" fontWeight={500} noWrap sx={{ display: 'block', color: 'text.primary', fontSize: '0.75rem' }}>
                                        {conv.title || 'New Conversation'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AccessTimeIcon sx={{ fontSize: 11, color: 'grey.400' }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                            {formatTime(conv.last_message_at)}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ))
                )}
            </Box>

            {/* Footer */}
            <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                    <Avatar
                        sx={{
                            width: 28,
                            height: 28,
                            bgcolor: 'primary.main',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                        }}
                    >
                        {guestName?.charAt(0) || 'G'}
                    </Avatar>
                    {!isCollapsed && (
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Typography variant="caption" fontWeight={500} noWrap display="block">
                                {guestName || 'Guest'}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer anchor="left" open={isOpen} onClose={onToggle} PaperProps={{ sx: { width: 260 } }}>
                {sidebarContent}
            </Drawer>
        );
    }

    return (
        <Box
            sx={{
                width: isCollapsed ? 56 : 240,
                flexShrink: 0,
                height: '100%',
                transition: 'width 0.3s',
                bgcolor: 'background.paper',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
            }}
        >
            {sidebarContent}
        </Box>
    );
}

// ============================================================
// MAIN PUBLIC CHAT SPACE
// ============================================================
export default function PublicChatSpace() {
    const { chatbotIdentity } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    // Auth state
    const [settings, setSettings] = useState(null);
    const [sessionToken, setSessionToken] = useState(() => localStorage.getItem(`cs_embed_token_${chatbotIdentity}`) || null);
    const [guestName, setGuestName] = useState(() => localStorage.getItem(`cs_embed_name_${chatbotIdentity}`) || '');
    const [isRegistering, setIsRegistering] = useState(false);
    const [loadingSettings, setLoadingSettings] = useState(true);

    // Layout state
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Citations state
    const [citations, setCitations] = useState([]);
    const [sourcesOpen, setSourcesOpen] = useState(false);
    const [sourcesCollapsed, setSourcesCollapsed] = useState(false);

    // Chat state
    const [currentSessionCode, setCurrentSessionCode] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isLoadingChat, setIsLoadingChat] = useState(false);
    const [privacy, setPrivacy] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    const isEmptyChat = messages.length === 0;
    const hasCitations = citations && citations.length > 0;

    // Load settings on mount
    useEffect(() => {
        loadSettings();
    }, [chatbotIdentity]);

    // Load conversation when session changes
    useEffect(() => {
        if (sessionToken && settings) loadConversation(currentSessionCode);
    }, [currentSessionCode, sessionToken, settings]);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const loadSettings = async () => {
        setLoadingSettings(true);
        try {
            const response = await axios.get(`${backendURL}/api/public/chatspace/settings`, {
                params: { chatbot_identity: chatbotIdentity },
            });
            if (response.data.success) {
                setSettings(response.data.data.settings);
            }
        } catch (error) {
            showSnackbar('Could not load chat settings.', 'error');
        } finally {
            setLoadingSettings(false);
        }
    };

    const handleRegister = async (name, email) => {
        setIsRegistering(true);
        try {
            const response = await axios.post(`${backendURL}/api/public/chatspace/register-guest`, {
                chatbot_identity: chatbotIdentity,
                guest_name: name,
                guest_email: email,
            });
            if (response.data.success) {
                const token = response.data.data.session_token;
                setSessionToken(token);
                setGuestName(name);
                localStorage.setItem(`cs_embed_token_${chatbotIdentity}`, token);
                localStorage.setItem(`cs_embed_name_${chatbotIdentity}`, name);
            }
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Registration failed.', 'error');
        } finally {
            setIsRegistering(false);
        }
    };

    const loadConversation = async (sc) => {
        if (!sessionToken) return;
        setIsLoadingChat(true);
        setCitations([]);
        setSourcesOpen(false);

        if (!sc) {
            setMessages([]);
            setIsLoadingChat(false);
            return;
        }

        try {
            const response = await axios.get(`${backendURL}/api/public/chatspace/history`, {
                params: { session_token: sessionToken, session_code: sc },
            });
            if (response.data.success) {
                const { history } = response.data.data;
                setMessages(history?.length > 0 ? history : []);
            }
        } catch (error) {
            showSnackbar('Could not load conversation.', 'error');
        } finally {
            setIsLoadingChat(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!input.trim() || !sessionToken) return;

        const userMessage = {
            id: `user-${Date.now()}`,
            message: input,
            is_from_user: true,
        };
        setMessages((prev) => [...prev, userMessage]);
        const currentInput = input;
        setInput('');
        if (textareaRef.current) textareaRef.current.style.height = 'auto';

        setIsSending(true);
        try {
            const payload = {
                session_token: sessionToken,
                message: currentInput,
                privacy,
            };
            if (currentSessionCode) payload.session_code = currentSessionCode;

            const response = await axios.post(`${backendURL}/api/public/chatspace/send-message`, payload);
            const botMessage = response.data.data;
            setMessages((prev) => [...prev, botMessage]);

            if (!currentSessionCode && botMessage.session_code) {
                setCurrentSessionCode(botMessage.session_code);
            }
        } catch (error) {
            showSnackbar(error.response?.data?.message || 'Failed to get a response.', 'error');
            setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
        } finally {
            setIsSending(false);
        }
    };

    const showSnackbar = (message, severity = 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        showSnackbar('Copied to clipboard!', 'success');
    };

    const handleCitationsClick = (msg) => {
        if (msg.citations?.length > 0) {
            setCitations(msg.citations);
            setSourcesOpen(true);
            setSourcesCollapsed(false);
        }
    };

    const handleCloseCitations = () => {
        setCitations([]);
        setSourcesOpen(false);
    };

    const handleConversationSelect = (sessionCode) => {
        setCurrentSessionCode(sessionCode || null);
        setCitations([]);
        setSourcesOpen(false);
        setSidebarOpen(false);
    };

    const handleInputChange = (e) => {
        setInput(e.target.value);
        const ta = e.target;
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    // Loading state
    if (loadingSettings) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: 'grey.100' }}>
                <CircularProgress />
            </Box>
        );
    }

    // Error state
    if (!settings) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: 'grey.100' }}>
                <Typography color="error">Chat Space not available.</Typography>
            </Box>
        );
    }

    // Registration form
    if (!sessionToken) {
        return <GuestRegistrationForm settings={settings} onRegister={handleRegister} isRegistering={isRegistering} />;
    }

    // Input area component
    const inputArea = (
        <Box sx={{ width: '100%' }}>
            <form onSubmit={handleSendMessage} style={{ position: 'relative' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 0.75,
                        '&:focus-within': {
                            borderColor: 'primary.main',
                            boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="How can I help you today?"
                        rows={1}
                        disabled={isSending}
                        style={{
                            flex: 1,
                            resize: 'none',
                            outline: 'none',
                            border: 'none',
                            background: 'transparent',
                            padding: '8px 12px',
                            fontSize: '0.9375rem',
                            fontFamily: 'inherit',
                            maxHeight: 200,
                            minHeight: 24,
                            lineHeight: 1.5,
                            color: '#1f2937',
                        }}
                    />
                    <IconButton
                        type="submit"
                        disabled={isSending || !input.trim()}
                        sx={{
                            bgcolor: settings.primary_color || 'primary.main',
                            color: settings.secondary_color || 'white',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            '&:hover': { bgcolor: settings.primary_color || 'primary.dark', filter: 'brightness(0.9)' },
                            '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' },
                        }}
                    >
                        <SendIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </form>
        </Box>
    );

    // Main Chat Interface
    return (
        <Box sx={{ display: 'flex', height: '100vh', width: '100%', bgcolor: 'grey.100', overflow: 'hidden', position: 'relative', p: 2, gap: 2 }}>
            {/* LEFT SIDEBAR - Desktop */}
            {!isMobile && (
                <PublicSidebar
                    isOpen={true}
                    isCollapsed={sidebarCollapsed}
                    onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                    onConversationSelect={handleConversationSelect}
                    currentSessionCode={currentSessionCode}
                    sessionToken={sessionToken}
                    guestName={guestName}
                />
            )}

            {/* Mobile Sidebar */}
            {isMobile && (
                <PublicSidebar
                    isOpen={sidebarOpen}
                    isCollapsed={false}
                    onToggle={() => setSidebarOpen(!sidebarOpen)}
                    onConversationSelect={handleConversationSelect}
                    currentSessionCode={currentSessionCode}
                    isMobile
                    sessionToken={sessionToken}
                    guestName={guestName}
                />
            )}

            {/* MAIN CONTENT */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 }}>
                {/* Top Bar */}
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {isMobile && (
                            <IconButton size="small" onClick={() => setSidebarOpen(true)}>
                                <MenuIcon sx={{ fontSize: 20, color: 'grey.600' }} />
                            </IconButton>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 2,
                                bgcolor: settings.primary_color || 'primary.main',
                                color: settings.secondary_color || 'white',
                            }}
                        >
                            <ForumIcon sx={{ fontSize: 16 }} />
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                {settings.chatbot_name || 'Chat Space'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={privacy}
                                    onChange={(e) => setPrivacy(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={<Typography variant="caption" color="text.secondary">Privacy</Typography>}
                            sx={{ ml: 0, mr: 0 }}
                        />
                        {hasCitations && (
                            <IconButton
                                size="small"
                                onClick={() => isMobile ? setSourcesOpen(!sourcesOpen) : setSourcesCollapsed(!sourcesCollapsed)}
                            >
                                <ViewSidebarIcon
                                    sx={{
                                        fontSize: 20,
                                        color: 'grey.500',
                                        transform: sourcesCollapsed ? 'rotate(180deg)' : 'none',
                                        transition: 'transform 0.3s',
                                    }}
                                />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {/* Chat Content */}
                <Box sx={{ flex: 1, overflow: 'hidden', borderRadius: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                bgcolor: 'background.paper',
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Empty state */}
                            {isEmptyChat && !isLoadingChat ? (
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, py: 4 }}>
                                    <Box sx={{ width: '100%', maxWidth: 560, mx: 'auto', textAlign: 'center' }}>
                                        <Avatar
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                bgcolor: settings.primary_color,
                                                color: settings.secondary_color,
                                                mx: 'auto',
                                                mb: 3,
                                                boxShadow: 3,
                                                borderRadius: 3,
                                            }}
                                        >
                                            <AutoAwesomeIcon sx={{ fontSize: 28 }} />
                                        </Avatar>
                                        <Typography variant="h5" fontWeight={600} gutterBottom>
                                            {settings.greeting_text || 'How can I help you today?'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                            Ask me anything, I'm here to help.
                                        </Typography>
                                        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                                            {inputArea}
                                        </Box>
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                    {/* Messages */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            overflow: 'auto',
                                            bgcolor: 'grey.50',
                                            '&::-webkit-scrollbar': { width: 8 },
                                            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                                            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 4, '&:hover': { bgcolor: 'grey.400' } },
                                            scrollbarWidth: 'thin',
                                            scrollbarColor: '#d1d5db transparent',
                                        }}
                                    >
                                        {isLoadingChat ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                                <CircularProgress size={28} />
                                            </Box>
                                        ) : (
                                            <Box sx={{ width: '90%', mx: 'auto', py: 3 }}>
                                                {messages.map((msg) => (
                                                    <ChatSpaceMessageBubble
                                                        key={msg.id}
                                                        msg={msg}
                                                        settings={settings}
                                                        onCitations={() => handleCitationsClick(msg)}
                                                        onCopy={handleCopy}
                                                    />
                                                ))}

                                                {isSending && (
                                                    <Box sx={{ display: 'flex', gap: 2, px: 2, py: 3 }}>
                                                        <Avatar
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                bgcolor: settings.primary_color,
                                                                color: settings.secondary_color,
                                                            }}
                                                        >
                                                            <SmartToyIcon sx={{ fontSize: 20 }} />
                                                        </Avatar>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                            {[0, 150, 300].map((delay) => (
                                                                <Box
                                                                    key={delay}
                                                                    sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        bgcolor: 'grey.400',
                                                                        animation: 'bounce 1.4s infinite both',
                                                                        animationDelay: `${delay}ms`,
                                                                        '@keyframes bounce': {
                                                                            '0%, 80%, 100%': { transform: 'scale(0)' },
                                                                            '40%': { transform: 'scale(1)' },
                                                                        },
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                                <div ref={messagesEndRef} />
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Footer input */}
                                    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0, px: 3, py: 2 }}>
                                        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                                            {inputArea}
                                        </Box>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* RIGHT SIDEBAR - Citations (Desktop) */}
            {hasCitations && !isMobile && (
                <Box
                    sx={{
                        width: sourcesCollapsed ? 56 : 240,
                        flexShrink: 0,
                        transition: 'width 0.3s',
                        height: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                    }}
                >
                    <ChatSpaceCitations
                        isOpen={true}
                        isCollapsed={sourcesCollapsed}
                        onToggle={() => setSourcesCollapsed(!sourcesCollapsed)}
                        onClose={handleCloseCitations}
                        citations={citations}
                    />
                </Box>
            )}

            {/* Mobile Citations Overlay */}
            {hasCitations && isMobile && (
                <ChatSpaceCitations
                    isOpen={sourcesOpen}
                    isCollapsed={false}
                    onToggle={() => setSourcesOpen(!sourcesOpen)}
                    onClose={() => { setSourcesOpen(false); handleCloseCitations(); }}
                    citations={citations}
                />
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
